const MAX_DIMENSION = 800;
const JPEG_QUALITY = 0.92;

// Re-encoding costs colour fidelity: a canvas round-trip flattens a wide-gamut photo
// to sRGB and compresses an already-compressed JPEG a second time. Files that are
// already small enough are uploaded untouched.
const PASS_THROUGH_MAX_BYTES = 2 * 1024 * 1024;

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image."));
    };
    image.src = url;
  });

export const scaledSize = (
  width: number,
  height: number,
  maxDimension: number = MAX_DIMENSION
): { width: number; height: number } => {
  const longest = Math.max(width, height);

  if (longest <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / longest;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

export const needsResizing = (
  width: number,
  height: number,
  bytes: number,
  maxDimension: number = MAX_DIMENSION,
  maxBytes: number = PASS_THROUGH_MAX_BYTES
): boolean => Math.max(width, height) > maxDimension || bytes > maxBytes;

/**
 * Downscales an image to fit within MAX_DIMENSION so uploads stay small and the server
 * needs no image library. A file that already fits is returned as-is, which keeps its
 * colour profile and avoids a second round of JPEG compression.
 */
export const resizeImage = async (file: File): Promise<Blob> => {
  const image = await loadImage(file);

  if (!needsResizing(image.naturalWidth, image.naturalHeight, file.size)) {
    return file;
  }

  const size = scaledSize(image.naturalWidth, image.naturalHeight);

  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("That image could not be processed in this browser.");
  }
  context.drawImage(image, 0, 0, size.width, size.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("That image could not be processed."))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });
};

export default { resizeImage, scaledSize, needsResizing };
